import Input from "../common/Input";
import Button from "../common/Button";
import DateSelector from "./DateSelector";
import TimeSlotPicker from "./TimeSlotPicker";
import {
  AUTO_MOBILE_PARTY_SIZE_THRESHOLD,
  HENNA_TYPE_OPTIONS,
  JAGUAR_HENNA_DISCLAIMER,
  HENNA_TYPES,
  LOCATION_NOTES_DESCRIPTION,
  MOBILE_MODE_DESCRIPTION,
  MOBILE_TRAVEL_EXPENSES_DISCLAIMER,
  SERVICE_MODE,
  STUDIO_MODE_DESCRIPTION,
} from "../../utils/constants";
import formatCurrency from "../../utils/formatCurrency";

const BookingForm = ({
  formData,
  errors = {},
  services = [],
  selectedService = null,
  calculatedPartySize = 0,
  calculatedBookingPrice = 0,
  calculatedBookingDurationMinutes = 0,
  onChange,
  onSubmit,
  onSelectSlot,
  availableDates = [],
  availableSlots = [],
  slotsLoading = false,
  loading = false,
  isGuest = false,
  submitLabel,
}) => {
  const resolvedSubmitLabel =
    submitLabel || (isGuest ? "Continue as Guest" : "Confirm Appointment");

  const isJaguarSelected = formData?.hennaType === HENNA_TYPES.JAGUAR;
  const isMobileMode = formData?.serviceMode === SERVICE_MODE.MOBILE;

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="content-card-lg p-6">
        <h2 className="text-xl font-semibold text-henna">Choose Your Service</h2>
        <p className="mt-2 text-sm leading-6 text-muted-warm">
          Select the service you would like, then choose the henna type for your
          appointment.
        </p>

        <div className="mt-5 space-y-5">
          <div>
            <label className="field-label" htmlFor="serviceId">
              Service <span className="text-red-500">*</span>
            </label>

            <select
              id="serviceId"
              name="serviceId"
              value={formData?.serviceId || ""}
              onChange={onChange}
              className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-[#2f2626] shadow-sm transition focus:outline-none focus:ring-2 ${
                errors?.serviceId
                  ? "border-red-400 focus:border-red-500 focus:ring-red-200"
                  : "border-[#d7c2a3] focus:border-[#8B3A3A] focus:ring-[#ead8c0]"
              }`}
            >
              <option value="">Select a service</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.serviceName}
                  {service.servicePrice !== undefined && service.servicePrice !== null
                    ? ` - $${Number(service.servicePrice).toFixed(2)}`
                    : ""}
                </option>
              ))}
            </select>

            {errors?.serviceId ? (
              <p className="field-error">{errors.serviceId}</p>
            ) : selectedService ? (
              <p className="field-hint">
                {selectedService.serviceDescription ||
                  "Your selected service is ready for the next step."}
              </p>
            ) : null}
          </div>

          <div>
            <label className="field-label" htmlFor="hennaType">
              Henna Type <span className="text-red-500">*</span>
            </label>

            <select
              id="hennaType"
              name="hennaType"
              value={formData?.hennaType || ""}
              onChange={onChange}
              className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-[#2f2626] shadow-sm transition focus:outline-none focus:ring-2 ${
                errors?.hennaType
                  ? "border-red-400 focus:border-red-500 focus:ring-red-200"
                  : "border-[#d7c2a3] focus:border-[#8B3A3A] focus:ring-[#ead8c0]"
              }`}
            >
              <option value="">Select a henna type</option>
              {HENNA_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                  {option === HENNA_TYPES.JAGUAR ? " (+$25)" : ""}
                </option>
              ))}
            </select>

            {errors?.hennaType ? (
              <p className="field-error">{errors.hennaType}</p>
            ) : (
              <p className="field-hint">
                Red Natural Henna keeps the original service price and timing.
              </p>
            )}
          </div>

          {isJaguarSelected ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-henna">Jaguar Henna Notice</p>
              <p className="mt-2 text-sm leading-6 text-muted-warm">
                {JAGUAR_HENNA_DISCLAIMER}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      <div className="content-card-lg p-6">
        <h2 className="text-xl font-semibold text-henna">Choose Date and Time</h2>
        <p className="mt-2 text-sm leading-6 text-muted-warm">
          Select a date from the calendar, then choose from the available time
          slots.
        </p>

        <div className="mt-6 grid gap-5 xl:grid-cols-[1.1fr_1.7fr]">
          <div className="min-w-0">
            <DateSelector
              value={formData?.bookingDate || ""}
              onChange={onChange}
              error={errors?.bookingDate}
              availableDates={availableDates}
            />
          </div>

          <div className="min-w-0">
            <TimeSlotPicker
              selectedDate={formData?.bookingDate}
              slots={availableSlots}
              selectedStartTime={formData?.startTime || ""}
              onSelectSlot={onSelectSlot}
              error={errors?.startTime || errors?.endTime}
              loading={slotsLoading}
            />
          </div>
        </div>

        {formData?.startTime && formData?.endTime ? (
          <div className="mt-6 rounded-2xl bg-soft-cream p-4">
            <p className="text-sm font-semibold text-henna">Selected Time</p>
            <p className="mt-2 text-sm text-muted-warm">
              {formData.startTime} - {formData.endTime}
            </p>
          </div>
        ) : null}
      </div>

      <div className="content-card-lg p-6">
        <h2 className="text-xl font-semibold text-henna">Appointment Details</h2>
        <p className="mt-2 text-sm leading-6 text-muted-warm">
          Add your party details and any notes that may help with your
          appointment.
        </p>

        <div className="mt-5 space-y-5">
          <div className="form-grid two">
            <Input
              label="Number of Adults"
              name="numOfAdults"
              type="number"
              min="0"
              value={formData?.numOfAdults ?? 0}
              onChange={onChange}
              error={errors?.numOfAdults}
              required
            />

            <Input
              label="Number of Children"
              name="numOfChildren"
              type="number"
              min="0"
              value={formData?.numOfChildren ?? 0}
              onChange={onChange}
              error={errors?.numOfChildren}
              required
            />
          </div>

          <div className="rounded-2xl bg-soft-cream p-4">
            <p className="text-sm font-semibold text-henna">Booking Overview</p>
            <div className="mt-3 space-y-2 text-sm text-muted-warm">
              <p>
                Total people in this appointment:{" "}
                <span className="font-semibold text-[#3b2f2f]">
                  {calculatedPartySize}
                </span>
              </p>
              <p>
                Henna type:{" "}
                <span className="font-semibold text-[#3b2f2f]">
                  {formData?.hennaType || "Select a henna type"}
                </span>
              </p>
              <p>
                Estimated total:{" "}
                <span className="font-semibold text-[#3b2f2f]">
                  {selectedService
                    ? formatCurrency(calculatedBookingPrice)
                    : "Select a service"}
                </span>
              </p>
              <p>
                Estimated duration:{" "}
                <span className="font-semibold text-[#3b2f2f]">
                  {selectedService
                    ? `${calculatedBookingDurationMinutes} minutes`
                    : "Select a service"}
                </span>
              </p>
            </div>
            {errors?.partySize ? <p className="field-error">{errors.partySize}</p> : null}
          </div>

          <Input
            label="Additional Notes"
            name="notes"
            value={formData?.notes || ""}
            onChange={onChange}
            error={errors?.notes}
            textarea
            rows={4}
            placeholder="Optional notes about your event, preferences, or anything you would like the artist to know."
          />
        </div>
      </div>

      <div className="content-card-lg p-6">
        <h2 className="text-xl font-semibold text-henna">Appointment Type</h2>
        <p className="mt-2 text-sm leading-6 text-muted-warm">
          Your appointment type is chosen automatically based on your total party
          size.
        </p>

        <div className="mt-5 space-y-4">
          <div className="rounded-2xl border border-[#d7c2a3] bg-soft-cream p-4">
            <p className="text-sm font-semibold text-henna">How the Booking Type Is Chosen</p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              If the total party size is less than {AUTO_MOBILE_PARTY_SIZE_THRESHOLD},
              your appointment is automatically set to{" "}
              <span className="font-semibold text-[#3b2f2f]">Studio</span>.
            </p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              If the total party size is {AUTO_MOBILE_PARTY_SIZE_THRESHOLD} or
              more, your appointment is automatically set to{" "}
              <span className="font-semibold text-[#3b2f2f]">Mobile</span>.
            </p>
          </div>

          <div className="rounded-2xl border border-goldlight bg-white p-4">
            <p className="text-base font-semibold text-henna">
              Current Appointment Type: {formData?.serviceMode || "Not selected"}
            </p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              {isMobileMode ? MOBILE_MODE_DESCRIPTION : STUDIO_MODE_DESCRIPTION}
            </p>

            {calculatedPartySize >= AUTO_MOBILE_PARTY_SIZE_THRESHOLD ? (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-[#6b4b1f]">
                Because your party size is {AUTO_MOBILE_PARTY_SIZE_THRESHOLD} or
                more, this appointment is automatically set to{" "}
                <span className="font-semibold">Mobile</span> so the artist can
                come to your location.
              </div>
            ) : (
              <div className="mt-3 rounded-xl border border-[#d7c2a3] bg-soft-cream px-4 py-3 text-sm text-muted-warm">
                For smaller appointments, this booking is automatically set to{" "}
                <span className="font-semibold text-[#3b2f2f]">Studio</span> so
                you can visit the artist.
              </div>
            )}

            {isMobileMode && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-[#6b4b1f]">
                <span className="font-semibold">Mobile appointment selected.</span>{" "}
                The artist will travel to your location, and the client is
                responsible for the artist&apos;s travel expenses.
              </div>
            )}
          </div>
        </div>

        {isMobileMode && (
          <div className="mt-6 space-y-5">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-henna">Mobile Appointment Details</p>
              <p className="mt-2 text-sm leading-6 text-muted-warm">
                {LOCATION_NOTES_DESCRIPTION}
              </p>
              <p className="mt-2 text-sm leading-6 text-muted-warm">
                {MOBILE_TRAVEL_EXPENSES_DISCLAIMER}
              </p>
            </div>

            <div className="form-grid two">
              <Input
                label="Address Line 1"
                name="onsiteAddress.line1"
                value={formData?.onsiteAddress?.line1 || ""}
                onChange={onChange}
                error={errors?.onsiteAddress?.line1}
                required
                placeholder="Street address"
              />

              <Input
                label="Address Line 2"
                name="onsiteAddress.line2"
                value={formData?.onsiteAddress?.line2 || ""}
                onChange={onChange}
                error={errors?.onsiteAddress?.line2}
                placeholder="Apartment, suite, unit, or building"
              />
            </div>

            <div className="form-grid three">
              <Input
                label="City"
                name="onsiteAddress.city"
                value={formData?.onsiteAddress?.city || ""}
                onChange={onChange}
                error={errors?.onsiteAddress?.city}
                required
              />

              <Input
                label="State"
                name="onsiteAddress.state"
                value={formData?.onsiteAddress?.state || ""}
                onChange={onChange}
                error={errors?.onsiteAddress?.state}
                required
              />

              <Input
                label="ZIP Code"
                name="onsiteAddress.zip"
                value={formData?.onsiteAddress?.zip || ""}
                onChange={onChange}
                error={errors?.onsiteAddress?.zip}
                required
              />
            </div>

            <Input
              label="Location Notes"
              name="locationNotes"
              value={formData?.locationNotes || ""}
              onChange={onChange}
              error={errors?.locationNotes}
              textarea
              rows={4}
              placeholder="Gate code, apartment number, parking guidance, landmarks, or arrival instructions."
              hint={LOCATION_NOTES_DESCRIPTION}
            />
          </div>
        )}
      </div>

      {isGuest && (
        <div className="content-card-lg p-6">
          <h2 className="text-xl font-semibold text-henna">Guest Details</h2>
          <p className="mt-2 text-sm leading-6 text-muted-warm">
            Please provide the guest information needed to complete the
            appointment.
          </p>

          <div className="mt-5 space-y-5">
            <div className="form-grid two">
              <Input
                label="First Name"
                name="guest.firstName"
                value={formData?.guest?.firstName || ""}
                onChange={onChange}
                error={errors?.guestFirstName}
                required
              />

              <Input
                label="Last Name"
                name="guest.lastName"
                value={formData?.guest?.lastName || ""}
                onChange={onChange}
                error={errors?.guestLastName}
                required
              />
            </div>

            <div className="form-grid two">
              <Input
                label="Email"
                name="guest.email"
                type="email"
                value={formData?.guest?.email || ""}
                onChange={onChange}
                error={errors?.guestEmail}
                required
              />

              <Input
                label="Phone Number"
                name="guest.phoneNumber"
                value={formData?.guest?.phoneNumber || ""}
                onChange={onChange}
                error={errors?.guestPhoneNumber}
                required
              />
            </div>
          </div>
        </div>
      )}

      <div className="content-card-lg p-6">
        <div className="flex justify-center">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            disabled={!formData?.startTime}
            className="min-w-[240px]"
          >
            {resolvedSubmitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default BookingForm;
